// src/pages/etc/IncomeStatus.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";
import styles from "./IncomeStatus.module.css";

export default function IncomeStatus() {
  const year = new Date().getFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  
  const [fromDate, setFromDate] = useState(yearStart);
  const [toDate, setToDate] = useState(yearEnd);
  const [tree, setTree] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [amountResults, setAmountResults] = useState({});
  const [partnerData, setPartnerData] = useState({});
  
  const sortKeysNumerically = (obj) => {
    const keys = Object.keys(obj);
    return keys.sort((a, b) => {
      const partsA = a.match(/\d+/g)?.map(Number) || [0];
      const partsB = b.match(/\d+/g)?.map(Number) || [0];
      for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const diff = (partsA[i] || 0) - (partsB[i] || 0);
        if (diff !== 0) return diff;
      }
      return a.localeCompare(b, "ko");
    });
  };
  
  const parseAmount = (v) => {
    const n = Number(String(v ?? "0").replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  
  const fetchAndSumAllPages = async (url, baseParams, labelForLog = "") => {
    let sum = 0;
    let page = 1;
    let safety = 0;
    while (true) {
      safety += 1;
      if (safety > 200) break;
      const params = { ...baseParams, page };
      try {
        const res = await api.get(url, { params });
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.results ?? [];
        data.forEach((r) => (sum += parseAmount(r?.amount)));
        const hasNext =
          res.data && typeof res.data === "object" && !!res.data.next;
        if (!hasNext || data.length === 0) break;
        page += 1;
      } catch (err) {
        console.warn(`[합계][${labelForLog}] 요청 실패`, err);
        break;
      }
    }
    return sum;
  };
  
  const getCafeSemokFromDraftNo = (draftNo) => {
    if (!draftNo) return null;
    if (draftNo.includes("CAFE_INN 가산점")) return "01 카페인 가산점";
    if (draftNo.includes("CAFE_INN 독산점")) return "02 카페인 독산점";
    if (draftNo.includes("CAFE_INN 금나래점")) return "03 카페인 금나래점";
    return null;
  };
  
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/accounting/accounts/general-sales/list/");
        const list = Array.isArray(data?.results) ? data.results : data;
        
        const grouped = {};
        list.forEach((item) => {
          const { category, gwang, hang, sehang, mok, semok, detail, sangse } = item;
          const _gwang = gwang || "기타관";
          const _hang = hang || "기타항";
          const _sehang = sehang || "기타세항";
          const _mok = mok || "기타목";
          const _semok = semok || "기타세목";
          
          let semokKey = semok || "기타세목";

          if (_semok.includes("CAFE_INN 가산점") || _mok.includes("CAFE_INN 가산점") || _sehang.includes("CAFE_INN 가산점"))
            semokKey = "01 카페인 가산점";
          if (_semok.includes("CAFE_INN 독산점") || _mok.includes("CAFE_INN 독산점") || _sehang.includes("CAFE_INN 독산점"))
            semokKey = "02 카페인 독산점";
          if (_semok.includes("CAFE_INN 금나래점") || _mok.includes("CAFE_INN 금나래점") || _sehang.includes("CAFE_INN 금나래점"))
            semokKey = "03 카페인 금나래점";
          if (_semok.includes("CAFE_INN 그린점") || _mok.includes("CAFE_INN 그린점") || _sehang.includes("CAFE_INN 그린점"))
            semokKey = "04 카페인 그린점";


          
          if (!grouped[_gwang]) grouped[_gwang] = {};
          if (!grouped[_gwang][_hang]) grouped[_gwang][_hang] = {};
          if (!grouped[_gwang][_hang][_sehang]) grouped[_gwang][_hang][_sehang] = {};
          if (!grouped[_gwang][_hang][_sehang][_mok])
            grouped[_gwang][_hang][_sehang][_mok] = {};
          if (!grouped[_gwang][_hang][_sehang][_mok][semokKey])
            grouped[_gwang][_hang][_sehang][_mok][semokKey] = {};

          // ✅ 경영팀(본사)인 경우: sangse가 있으면 sangse, 없으면 semok
          // 나머지: detail 사용
          let detailKey;
          if (category && category.includes("본사")) {
            detailKey = sangse?.trim() || _semok;
          } else {
            detailKey = detail;
          }

          if (detailKey && !grouped[_gwang][_hang][_sehang][_mok][semokKey][detailKey]) {
            grouped[_gwang][_hang][_sehang][_mok][semokKey][detailKey] = [];
          }

          if (detailKey && sangse && !grouped[_gwang][_hang][_sehang][_mok][semokKey][detailKey].includes(sangse)) {
            grouped[_gwang][_hang][_sehang][_mok][semokKey][detailKey].push(sangse);
          }
        });
        
        const amounts = {};
        const partners = {};
        const commonDate = { start_date: fromDate, end_date: toDate };
        
        const tasks = [
          (async () => {
            try {
              const params = { category: "환경/소독", ...commonDate };
              console.log("🔍 [환경/소독] 요청 파라미터:", params);
              console.log("🔍 [환경/소독] fromDate:", fromDate, "toDate:", toDate);
              
              const res = await api.get("/income-drafts/", { params });
              const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
              
              console.log(`🔍 [환경/소독] 응답 데이터 수: ${list.length}개`);
              if (list.length > 0) {
                console.log("🔍 [환경/소독] 첫 3개 항목의 날짜:");
                list.slice(0, 3).forEach((item, idx) => {
                  console.log(`   ${idx + 1}. date: ${item.date || item.created_at || item.issue_date}, amount: ${item.amount}`);
                });
              }
              
              let sum = 0;
              const partnerMap = {};
              
              list.forEach((item) => {
                const amt = parseAmount(item.amount);
                const partnerName = item?.partner?.name || item?.partner_name || "미지정";
                
                sum += amt;
                if (!partnerMap[partnerName]) partnerMap[partnerName] = 0;
                partnerMap[partnerName] += amt;
              });
              
              amounts["소독방역"] = sum;
              partners["소독"] = Object.entries(partnerMap).map(([name, amount]) => ({
                name,
                amount
              }));
              
              console.log(`✅ 소독방역 합계: ${sum.toLocaleString()}원`);
            } catch (err) {
              console.warn("환경/소독 로딩 실패", err);
            }
          })(),

          (async () => {
            try {
              const params = { 
                category: "환경/종합",
                ...commonDate 
              };
              console.log("🔍 [환경/종합] 요청 파라미터:", params);
              console.log("🔍 [환경/종합] fromDate:", fromDate, "toDate:", toDate);
              
              const res = await api.get("/env-total-incomes/", { params });
              const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
              
              console.log(`🔍 [환경/종합] 응답 데이터 수: ${list.length}개`);
              if (list.length > 0) {
                console.log("🔍 [환경/종합] 첫 3개 항목의 날짜:");
                list.slice(0, 3).forEach((item, idx) => {
                  console.log(`   ${idx + 1}. date: ${item.date || item.created_at || item.service_date}, subcategory: ${item.subcategory}, amount: ${item.amount}`);
                });
              }
              
              const subPartners = {};
              
              for (const row of list) {
                const sub = row.subcategory?.trim() || "기타";
                const amt = parseAmount(row.amount);
                const partnerName = row?.partner?.name || row?.partner_name || 
                                   row?.vendor?.name || row?.vendor_name || "미지정";
                
                amounts[sub] = (amounts[sub] || 0) + amt;
                
                if (!subPartners[sub]) subPartners[sub] = {};
                if (!subPartners[sub][partnerName]) subPartners[sub][partnerName] = 0;
                subPartners[sub][partnerName] += amt;
              }
              
              Object.entries(subPartners).forEach(([sub, partnerMap]) => {
                partners[sub] = Object.entries(partnerMap).map(([name, amount]) => ({
                  name,
                  amount
                }));
              });
              
              console.log(`✅ 환경/종합 로딩 완료: ${Object.keys(amounts).filter(k => k !== "소독방역").length}개 항목`);
              console.log(`✅ 환경/종합 세부항목:`, Object.keys(subPartners));
            } catch (err) {
              console.warn("환경/종합 로딩 실패", err);
            }
          })(),


          (async () => {
            try {
              const res = await api.get("/income-service/", { params: { ...commonDate } });
              const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
              
              let sum = 0;
              const partnerMap = {};
              
              list.forEach((item) => {
                const amt = parseAmount(item.amount);
                const partnerName = item?.partner_name || item?.partner?.name || "미지정";
                
                sum += amt;
                if (!partnerMap[partnerName]) partnerMap[partnerName] = 0;
                partnerMap[partnerName] += amt;
              });
              
              amounts["용역사업"] = sum;
              partners["용역"] = Object.entries(partnerMap).map(([name, amount]) => ({
                name,
                amount
              }));
              
              console.log(`✅ 용역사업: ${sum?.toLocaleString()}원, 거래처 ${Object.keys(partnerMap).length}개`);
            } catch (err) {
              console.warn("용역사업 로딩 실패", err);
            }
          })(),
          
          (async () => {
            try {
              let page = 1;
              let hasNext = true;
              const subPartners = {};
              
              console.log("🔍 [경영팀] API 호출 시작");
              
              while (hasNext && page <= 200) {
                const res = await api.get("/income/business/", { 
                  params: { ...commonDate, page } 
                });
                
                const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                
                console.log(`🔍 [경영팀] 페이지 ${page}: ${list.length}개 항목`);
                
                if (page === 1) {
                  list.slice(0, 3).forEach((item, idx) => {
                    console.log(`   📌 항목 ${idx + 1} subcategory: "${item.subcategory}"`);
                  });
                }
                
                list.forEach((item) => {
                  const sub = item.subcategory?.trim() || "기타";
                  const amt = parseAmount(item.amount);
                  const partnerName = item?.partner?.name || item?.partner_name || "미지정";
                  
                  amounts[sub] = (amounts[sub] || 0) + amt;
                  
                  if (!subPartners[sub]) subPartners[sub] = {};
                  if (!subPartners[sub][partnerName]) subPartners[sub][partnerName] = 0;
                  subPartners[sub][partnerName] += amt;
                });
                
                hasNext = res.data && typeof res.data === "object" && !!res.data.next;
                console.log(`🔍 [경영팀] 페이지 ${page} 완료, 다음 페이지 존재: ${hasNext}`);
                page += 1;
              }
              
              Object.entries(subPartners).forEach(([sub, partnerMap]) => {
                partners[sub] = Object.entries(partnerMap).map(([name, amount]) => ({
                  name,
                  amount
                }));
              });
              
              console.log(`✅ 경영팀 최종 결과:`);
              console.log(`   - 세부구분 개수: ${Object.keys(subPartners).length}개`);
              console.log(`   - 세부구분 목록:`, Object.keys(subPartners));
              console.log(`   - amounts 키 목록:`, Object.keys(amounts).filter(k => 
                Object.keys(subPartners).includes(k)
              ));
              console.log(`   - partners 키 목록:`, Object.keys(partners).filter(k => 
                Object.keys(subPartners).includes(k)
              ));
            } catch (err) {
              console.error("❌ 경영팀 로딩 실패", err);
            }
          })(),

          (async () => {
            try {
              let sum = 0;
              let page = 1;
              let hasNext = true;
              let totalItems = 0;
              
              console.log("🔍 [가산점] API 호출 시작");
              
              while (hasNext && page <= 200) {
                const res = await api.get("/income/cafe/gasan/", {
                  params: { ...commonDate, page }
                });
                
                const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                
                console.log(`🔍 [가산점] 페이지 ${page}: ${list.length}개 항목`);
                
                list.forEach((item, idx) => {
                  const amt = parseAmount(item.amount);
                  sum += amt;
                  totalItems += 1;
                  
                  if (idx < 3) {
                    console.log(`   - 항목 ${idx + 1}: ${amt.toLocaleString()}원`, item);
                  }
                });
                
                hasNext = res.data && typeof res.data === "object" && !!res.data.next;
                console.log(`🔍 [가산점] 페이지 ${page} 완료, 다음 페이지 존재: ${hasNext}`);
                page += 1;
              }
              
              amounts["01 카페인 가산점"] = sum;
              
              console.log(`✅ 카페인 가산점 최종 결과:`);
              console.log(`   - 총 합계: ${sum?.toLocaleString()}원`);
              console.log(`   - 총 페이지: ${page - 1}`);
              console.log(`   - 총 항목 수: ${totalItems}`);
              console.log(`   - amounts 객체에 저장된 값:`, amounts["01 카페인 가산점"]);
            } catch (err) {
              console.error("❌ 카페인 가산점 로딩 실패", err);
            }
          })(),
          
          (async () => {
            try {
              let sum = 0;
              let page = 1;
              let hasNext = true;
              
              while (hasNext && page <= 200) {
                const res = await api.get("/income/cafe/doksan/", {
                  params: { ...commonDate, page }
                });
                
                const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                
                list.forEach((item) => {
                  const amt = parseAmount(item.amount);
                  sum += amt;
                });
                
                hasNext = res.data && typeof res.data === "object" && !!res.data.next;
                page += 1;
              }
              
              amounts["02 카페인 독산점"] = sum;
              
              console.log(`✅ 카페인 독산점: ${sum?.toLocaleString()}원 (총 ${page - 1}페이지)`);
            } catch (err) {
              console.warn("카페인 독산점 로딩 실패", err);
            }
          })(),
          
          (async () => {
            try {
              let sum = 0;
              let page = 1;
              let hasNext = true;
              
              while (hasNext && page <= 200) {
                const res = await api.get("/income/cafe/geumnarae/", {
                  params: { ...commonDate, page }
                });
                
                const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
                
                list.forEach((item) => {
                  const amt = parseAmount(item.amount);
                  sum += amt;
                });
                
                hasNext = res.data && typeof res.data === "object" && !!res.data.next;
                page += 1;
              }
              
              // after — 금나래점 })(), 다음, 닫는 ]; 앞에 그린점 task 삽입
              amounts["03 카페인 금나래점"] = sum;
              console.log(`✅ 카페인 금나래점: ${sum?.toLocaleString()}원 (총 ${page - 1}페이지)`);
            } catch (err) {
              console.warn("카페인 금나래점 로딩 실패", err);
            }
          })(),

          (async () => {
            try {
              let sum = 0;
              let page = 1;
              let hasNext = true;

              while (hasNext && page <= 200) {
                const res = await api.get("/income/cafe/green/", {
                  params: { ...commonDate, page }
                });

                const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];

                list.forEach((item) => {
                  const amt = parseAmount(item.amount);
                  sum += amt;
                });

                hasNext = res.data && typeof res.data === "object" && !!res.data.next;
                page += 1;
              }

              amounts["04 카페인 그린점"] = sum;

              console.log(`✅ 카페인 그린점: ${sum?.toLocaleString()}원 (총 ${page - 1}페이지)`);
            } catch (err) {
              console.warn("카페인 그린점 로딩 실패", err);
            }
          })(),
        ];


        await Promise.all(tasks);
        
        amounts["621-02 상품판매수익"] = 
          (amounts["01 카페인 가산점"] || 0) + 
          (amounts["02 카페인 독산점"] || 0) + 
          (amounts["03 카페인 금나래점"] || 0) +
          (amounts["04 카페인 그린점"] || 0);
        
        console.log("🎯 최종 amounts:", amounts);
        console.log("🎯 거래처별 데이터:", partners);
        
        const expandAll = {};
        const markExpanded = (obj, prefix = "") => {
          Object.keys(obj).forEach((key) => {
            const id = `${prefix}${key}`;
            expandAll[id] = true;
            if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
              markExpanded(obj[key], `${id}-`);
            }
          });
        };
        markExpanded(grouped);
        
        setTree(grouped);
        setExpanded(expandAll);
        setAmountResults(amounts);
        setPartnerData(partners);
      } catch (err) {
        console.error("❌ 데이터 불러오기 실패:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fromDate, toDate]);
  
  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  
  const renderTree = (node, level = 1, prefix = "") => {
    if (!node) return null;
    
    if (Array.isArray(node)) {
      return node.length > 0 ? (
        node.map((s, i) => (
          <div key={`${prefix}-s-${i}`} className={`${styles.row} ${styles[`level${level}`]}`}>
            <div className={`${styles.cell} ${styles[`indent${level}`]}`}>
              🔸 {s}
            </div>
            <div className={styles.cellRight}>0 원</div>
            <div className={styles.cellRight}>0 원</div>
            <div className={styles.cellNote}></div>
          </div>
        ))
      ) : null;
    }
  
    return sortKeysNumerically(node).map((key) => {
      const value = node[key];
      const id = `${prefix}${key}`;
      
      const isDetailLevel = Array.isArray(value);
      if (isDetailLevel) {
        let amount = 0;
        
        console.log(`🔍 [renderTree detail] key="${key}", prefix="${prefix}"`);
  
        // ✅ "1. 판매"인 경우 부모 카페 점포의 전체 금액 사용
        if (key === "1. 판매") {
          if (prefix.includes("01 카페인 가산점")) {
            amount = amountResults["01 카페인 가산점"] || 0;
            console.log(`   ✅ [1. 판매] 가산점 금액: ${amount.toLocaleString()}원`);
          } else if (prefix.includes("02 카페인 독산점")) {
            amount = amountResults["02 카페인 독산점"] || 0;
            console.log(`   ✅ [1. 판매] 독산점 금액: ${amount.toLocaleString()}원`);

          } else if (prefix.includes("03 카페인 금나래점")) {
            amount = amountResults["03 카페인 금나래점"] || 0;
            console.log(`   ✅ [1. 판매] 금나래점 금액: ${amount.toLocaleString()}원`);
          } else if (prefix.includes("04 카페인 그린점")) {
            amount = amountResults["04 카페인 그린점"] || 0;
          }


        } else if (prefix.includes("02 환경사업")) {
          if (key === "소독") {
            amount = amountResults["소독방역"] || 0;
          } else {
            amount = amountResults[key] || 0;
          }
        } else if (key === "용역") {
          amount = amountResults["용역사업"] || 0;
        } else {
          console.log(`   🔍 amountResults에서 "${key}" 찾기 시도...`);
          
          if (amountResults[key]) {
            amount = amountResults[key];
            console.log(`   ✅ 정확히 일치: ${amount.toLocaleString()}원`);
          } else {
            console.log(`   ❌ 정확한 이름 없음. 유사 검색 시작...`);
            console.log(`   📋 amountResults 전체 키:`, Object.keys(amountResults));
            
            const normalizedKey = key.replace(/\s/g, "");
            const foundKey = Object.keys(amountResults).find((k) => {
              const normalizedK = k.replace(/\s/g, "");
              const match = normalizedK === normalizedKey || 
                           normalizedK.includes(normalizedKey) || 
                           normalizedKey.includes(normalizedK);
              if (match) {
                console.log(`   🎯 매칭 성공: DB "${key}" → API "${k}"`);
              }
              return match;
            });
            
            if (foundKey) {
              amount = amountResults[foundKey];
              console.log(`   ✅ 유사 매칭: ${amount.toLocaleString()}원`);
            } else {
              console.log(`   ❌ 매칭 실패: 금액 0원`);
            }
          }
        }

        const detailKey = key === "소독" ? "소독" : key === "용역" ? "용역" : key;
        
        console.log(`   🔍 partnerData에서 "${detailKey}" 찾기 시도...`);
        console.log(`   📋 partnerData 전체 키:`, Object.keys(partnerData));
        
        let actualDetailKey = detailKey;
        if (!partnerData[detailKey]) {
          console.log(`   ❌ 정확한 이름 없음. 유사 검색 시작...`);
          
          const normalizedKey = detailKey.replace(/\s/g, "");
          const foundKey = Object.keys(partnerData).find((k) => {
            const normalizedK = k.replace(/\s/g, "");
            return normalizedK === normalizedKey || 
                   normalizedK.includes(normalizedKey) || 
                   normalizedKey.includes(normalizedK);
          });
          
          if (foundKey) {
            actualDetailKey = foundKey;
            console.log(`   🎯 거래처 매칭 성공: DB "${detailKey}" → API "${foundKey}"`);
          } else {
            console.log(`   ❌ 거래처 매칭 실패`);
          }
        } else {
          console.log(`   ✅ 정확히 일치: 거래처 ${partnerData[detailKey].length}개`);
        }
        
        const hasPartners = partnerData[actualDetailKey] && partnerData[actualDetailKey].length > 0;
        const hasSangse = value && value.length > 0;
        const hasChildren = hasPartners || hasSangse;
        const detailId = `detail-${prefix}-${key}`;
        
        return (
          <div key={id}>
            <div 
              className={`${styles.row} ${styles[`level${level}`]}`}
              onClick={() => hasChildren && toggleExpand(detailId)}
              style={{ cursor: hasChildren ? "pointer" : "default" }}
            >
              <div className={`${styles.cell} ${styles[`indent${level}`]}`}>
                {hasChildren && (
                  <span className={styles.arrow}>
                    {expanded[detailId] ? "▼" : "▶"}
                  </span>
                )}
                🔹 {key}
                {hasPartners && (
                  <span
                    style={{
                      color: "#2563eb",
                      fontSize: "12px",
                      marginLeft: 6,
                    }}
                  >
                    (거래처 {partnerData[actualDetailKey].length}개소)
                  </span>
                )}
              </div>
              <div className={styles.cellRight}>
                {amount.toLocaleString()} 원
              </div>
              <div className={styles.cellRight}>0 원</div>
              <div className={styles.cellNote}></div>
            </div>
  
            {hasPartners && expanded[detailId] && (
              <div className={styles.partnerBox}>
                {partnerData[actualDetailKey].map((p, idx) => (
                  <div
                    key={idx}
                    className={`${styles.row} ${styles[`level${level + 1}`]}`}
                  >
                    <div
                      className={`${styles.cell} ${styles[`indent${level + 1}`]}`}
                    >
                      🏢 {p.name}
                    </div>
                    <div className={styles.cellRight}>
                      {p.amount.toLocaleString()} 원
                    </div>
                    <div className={styles.cellRight}>0 원</div>
                    <div className={styles.cellNote}></div>
                  </div>
                ))}
              </div>
            )}
            
            {expanded[detailId] && value && value.length > 0 && (
              <div className={styles.child}>
                {renderTree(value, level + 1, `${id}-`)}
              </div>
            )}
          </div>
        );
      }
      
      const hasChildren = typeof value === "object" && Object.keys(value).length > 0;
      let totalAmount = 0;
      
      const calculateSum = (obj, parentSemokKey = "") => {
        let sum = 0;
        
        Object.entries(obj).forEach(([k, v]) => {
          if (Array.isArray(v)) {
            if (k === "소독") {
              sum += amountResults["소독방역"] || 0;
            } else if (k === "용역") {
              sum += amountResults["용역사업"] || 0;
            } else if (k === "경영팀") {
              sum += amountResults["경영팀"] || 0;
            } else if (k === "1. 판매") {
              if (parentSemokKey.includes("01 카페인 가산점")) {
                sum += amountResults["01 카페인 가산점"] || 0;
              } else if (parentSemokKey.includes("02 카페인 독산점")) {
                sum += amountResults["02 카페인 독산점"] || 0;

              } else if (parentSemokKey.includes("03 카페인 금나래점")) {
                sum += amountResults["03 카페인 금나래점"] || 0;
              } else if (parentSemokKey.includes("04 카페인 그린점")) {
                sum += amountResults["04 카페인 그린점"] || 0;
              }


            } else if (amountResults[k]) {
              sum += amountResults[k];
            } else {
              const foundKey = Object.keys(amountResults).find(
                (rk) => rk.includes(k) || k.includes(rk)
              );
              if (foundKey) {
                sum += amountResults[foundKey] || 0;
              }
            }
            return;
          } else if (typeof v === "object" && Object.keys(v).length > 0) {
            const firstValue = Object.values(v)[0];
            
            if (Array.isArray(firstValue)) {
              sum += calculateSum(v, k);
            } else {
              sum += calculateSum(v, parentSemokKey);
            }
          }
        });
        
        return sum;
      };
      
      if (hasChildren && typeof value === "object") {
        totalAmount = calculateSum(value, key);
        
        if (["01 카페인 가산점", "02 카페인 독산점", "03 카페인 금나래점", "04 카페인 그린점"].includes(key)) {


          console.log(`🔍 [renderTree] ${key} 렌더링 시작`);
          console.log(`   - calculateSum 결과: ${totalAmount.toLocaleString()}원`);
          console.log(`   - amountResults[${key}]:`, amountResults[key]);
          totalAmount = amountResults[key] || 0;
          console.log(`   - 최종 표시 금액: ${totalAmount.toLocaleString()}원`);
        }
      } else if (amountResults[key]) {
        totalAmount = amountResults[key];
      }

      return (
        <React.Fragment key={id}>
          <div
            className={`${styles.row} ${styles[`level${level}`]}`}
            onClick={() => hasChildren && toggleExpand(id)}
          >
            <div className={`${styles.cell} ${styles[`indent${level}`]}`}>
              {hasChildren && (
                <span className={styles.arrow}>{expanded[id] ? "▼" : "▶"}</span>
              )}
              {key}
            </div>
            <div className={styles.cellRight}>{totalAmount.toLocaleString()} 원</div>
            <div className={styles.cellRight}>0 원</div>
            <div className={styles.cellNote}></div>
          </div>
  
          {hasChildren && expanded[id] && (
            <div className={styles.child}>{renderTree(value, level + 1, `${id}-`)}</div>
          )}
        </React.Fragment>
      );
    });
  };
  
  return (
    <div className={styles.wrap}>
      <div className={styles.filterBar}>
        <label>기간:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className={styles.dateInput}
        />
        <span className={styles.dateDash}>~</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className={styles.dateInput}
        />
      </div>
      
      <div className={styles.headerRow}>
        <div className={styles.headerCell}>구분</div>
        <div className={styles.headerCell}>청구한 금액</div>
        <div className={styles.headerCell}>입금된 금액</div>
        <div className={styles.headerCell}>비고</div>
      </div>
      
      {loading ? (
        <div className={styles.loading}>불러오는 중...</div>
      ) : Object.keys(amountResults).length === 0 ? (
        <div className={styles.loading}>금액 계산 중...</div>
      ) : (
        <div className={styles.tree}>{renderTree(tree)}</div>
      )}
    </div>
  );
}

